import {
  buildFourStopGradient,
  DEFAULT_WEBSITE_THEME_ID,
  WEBSITE_CUSTOM_THEMES_KEY,
  WEBSITE_THEME_STORAGE_KEY,
  websiteThemePresetMap,
  WebsiteThemePreset,
} from "src/themes/presets";

export const hexToRgbTriplet = (hex: string): string => {
  const normalized = hex.replace("#", "").trim();
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized.slice(0, 6);

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);

  if ([r, g, b].some((channel) => Number.isNaN(channel))) {
    return "15 52 96";
  }

  return `${r} ${g} ${b}`;
};

export const normalizeHex = (value: string): string => {
  const cleaned = value.replace("#", "").trim();
  if (cleaned.length === 3) {
    return `#${cleaned
      .split("")
      .map((char) => char + char)
      .join("")
      .toUpperCase()}`;
  }
  if (cleaned.length >= 6) {
    return `#${cleaned.slice(0, 6).toUpperCase()}`;
  }
  return "#0F3460";
};

export const getCustomThemes = (): WebsiteThemePreset[] => {
  try {
    const raw = localStorage.getItem(WEBSITE_CUSTOM_THEMES_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as WebsiteThemePreset[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveCustomTheme = (preset: WebsiteThemePreset): void => {
  const existing = getCustomThemes().filter((item) => item.id !== preset.id);
  const next = [{ ...preset, isCustom: true }, ...existing].slice(0, 12);
  localStorage.setItem(WEBSITE_CUSTOM_THEMES_KEY, JSON.stringify(next));
};

export const getAllThemePresets = (): WebsiteThemePreset[] => {
  return [...getCustomThemes(), ...Object.values(websiteThemePresetMap)];
};

export const getThemePresetById = (themeId: string): WebsiteThemePreset | null => {
  const custom = getCustomThemes().find((item) => item.id === themeId);
  if (custom) {
    return custom;
  }
  return websiteThemePresetMap[themeId] || null;
};

export const getStoredWebsiteThemeId = (): string => {
  try {
    const saved = localStorage.getItem(WEBSITE_THEME_STORAGE_KEY);
    if (saved && getThemePresetById(saved)) {
      return saved;
    }
  } catch {
    // ignore storage errors
  }

  return DEFAULT_WEBSITE_THEME_ID;
};

export const getWebsiteThemePreset = (
  themeId?: string,
): WebsiteThemePreset => {
  const id = themeId || getStoredWebsiteThemeId();
  return (
    getThemePresetById(id) ||
    websiteThemePresetMap[DEFAULT_WEBSITE_THEME_ID]
  );
};

const mixHex = (startHex: string, endHex: string, amount: number): string => {
  const start = hexToRgbTriplet(startHex).split(" ").map(Number);
  const end = hexToRgbTriplet(endHex).split(" ").map(Number);
  const mixed = start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * amount),
  );

  return `#${mixed
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
};

export const buildCustomThemePreset = ({
  name,
  primary,
  secondary,
}: {
  name: string;
  primary: string;
  secondary: string;
}): WebsiteThemePreset => {
  const primaryHex = normalizeHex(primary);
  const secondaryHex = normalizeHex(secondary);
  const id = `custom-${Date.now()}`;
  const colors: [string, string, string, string] = [
    primaryHex,
    mixHex(primaryHex, secondaryHex, 0.34),
    mixHex(primaryHex, secondaryHex, 0.66),
    secondaryHex,
  ];

  return {
    id,
    name: name.trim() || "Custom Theme",
    category: "featured",
    colors,
    primary: primaryHex,
    secondary: secondaryHex,
    sidebar: colors[1],
    surface: colors[1],
    base: colors[1],
    description: "Your custom dual-color branding theme.",
    isCustom: true,
  };
};

export const applyWebsiteThemePreset = (
  preset: WebsiteThemePreset,
): WebsiteThemePreset => {
  const root = document.documentElement;
  const [color1, color2, color3, color4] = preset.colors;
  const verticalGradient = buildFourStopGradient(preset.colors, "180deg");
  const diagonalGradient = buildFourStopGradient(preset.colors, "135deg");

  root.style.setProperty("--color-primary", hexToRgbTriplet(preset.primary));
  root.style.setProperty(
    "--color-secondary",
    hexToRgbTriplet(preset.secondary),
  );
  root.style.setProperty("--color-sidebar", hexToRgbTriplet(preset.sidebar));
  root.style.setProperty("--color-surface", hexToRgbTriplet(preset.surface));
  root.style.setProperty("--color-theme-1", hexToRgbTriplet(color1));
  root.style.setProperty("--color-theme-2", hexToRgbTriplet(color2));
  root.style.setProperty("--color-theme-3", hexToRgbTriplet(color3));
  root.style.setProperty("--color-theme-4", hexToRgbTriplet(color4));
  root.style.setProperty("--color-gradient-start", hexToRgbTriplet(color1));
  root.style.setProperty("--color-gradient-end", hexToRgbTriplet(color4));

  root.style.setProperty("--primary-color", preset.primary);
  root.style.setProperty("--secondary-color", preset.secondary);
  root.style.setProperty("--theme-color-1", color1);
  root.style.setProperty("--theme-color-2", color2);
  root.style.setProperty("--theme-color-3", color3);
  root.style.setProperty("--theme-color-4", color4);
  root.style.setProperty("--gradient-color", verticalGradient);
  root.style.setProperty("--gradient-color-diagonal", diagonalGradient);

  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) {
    themeMeta.setAttribute("content", preset.primary);
  }

  try {
    localStorage.setItem(WEBSITE_THEME_STORAGE_KEY, preset.id);
  } catch {
    // ignore storage errors
  }

  return preset;
};

export const applyWebsiteTheme = (themeId: string): WebsiteThemePreset => {
  const preset =
    getThemePresetById(themeId) ||
    websiteThemePresetMap[DEFAULT_WEBSITE_THEME_ID];
  return applyWebsiteThemePreset(preset);
};

export const initWebsiteTheme = (): WebsiteThemePreset => {
  return applyWebsiteTheme(getStoredWebsiteThemeId());
};
